import { useState, useEffect, useCallback } from 'react'
import { FiMapPin, FiAlertCircle, FiExternalLink, FiSettings, FiList, FiMap } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { PageHeader, Btn } from '../../ui'
import MapaFichajesLeaflet from '../../components/MapaFichajesLeaflet'

/*
 * MapaFichajes (V2) — vista admin de fichajes con geolocalización.
 * Combina configuración del modo geo (desactivada/opcional/obligatoria)
 * con listado/mapa de fichajes que tienen coords.
 *
 * Por ahora muestra una tabla con enlace a Google Maps por marcaje.
 * Si más adelante quieres mapa real → integrar Leaflet aquí.
 */
const MODOS = [
  { id: 'desactivada', label: 'Desactivada', desc: 'No se solicita ubicación al fichar.' },
  { id: 'opcional',    label: 'Opcional',    desc: 'Se captura si el navegador la permite, pero no se exige.' },
  { id: 'obligatoria', label: 'Obligatoria', desc: 'Si el usuario niega permiso, no podrá fichar.' }
]

const hoy = () => new Date().toISOString().slice(0, 10)
const haceXdias = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10) }

const MapaFichajesV2 = () => {
  const [modo, setModo] = useState('desactivada')
  const [cargandoModo, setCargandoModo] = useState(true)
  const [guardandoModo, setGuardandoModo] = useState(false)

  const [fechaDesde, setFechaDesde] = useState(haceXdias(7))
  const [fechaHasta, setFechaHasta] = useState(hoy())
  const [fichajes, setFichajes] = useState([])
  const [cargandoFichajes, setCargandoFichajes] = useState(false)
  const [vista, setVista] = useState('mapa')

  useEffect(() => {
    api.get('/geolocalizacion/config').then(r => setModo(r.data?.modo || 'desactivada')).finally(() => setCargandoModo(false))
  }, [])

  const cargar = useCallback(async () => {
    setCargandoFichajes(true)
    try {
      const r = await api.get('/geolocalizacion/fichajes', {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta }
      })
      setFichajes(r.data || [])
    } catch (e) {
      toast.error(e.message || 'Error cargando fichajes')
    } finally {
      setCargandoFichajes(false)
    }
  }, [fechaDesde, fechaHasta])

  useEffect(() => { cargar() }, [cargar])

  const guardarModo = async (nuevo) => {
    setGuardandoModo(true)
    try {
      await api.put('/geolocalizacion/config', { modo: nuevo })
      setModo(nuevo)
      toast.success('Modo guardado')
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar')
    } finally {
      setGuardandoModo(false)
    }
  }

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-5">
      <PageHeader
        num="§ 06"
        kicker="Geolocalización"
        title="Marcajes con"
        italic="ubicación"
        sub="Configura el modo y revisa dónde se han registrado los fichajes."
      />

      {/* Configuración del modo */}
      <div className="ui-card p-5 space-y-3">
        <h3 className="font-display text-[16px] text-[var(--ink)] flex items-center gap-2">
          <FiSettings size={14} className="text-[var(--ink-2)]" /> Modo de captura
        </h3>
        {cargandoModo ? (
          <p className="text-[12px] text-[var(--ink-3)]">Cargando…</p>
        ) : (
          <div className="grid sm:grid-cols-3 gap-2">
            {MODOS.map((m) => (
              <button
                key={m.id}
                onClick={() => guardarModo(m.id)}
                disabled={guardandoModo || modo === m.id}
                className={`p-3 text-left rounded-soft border transition ${
                  modo === m.id
                    ? 'border-[var(--brand-1)] bg-[rgba(37,99,235,.06)]'
                    : 'border-[var(--line)] hover:bg-[var(--bg-2)]'
                }`}
              >
                <div className="text-[13px] font-medium text-[var(--ink)]">{m.label}</div>
                <div className="text-[11px] text-[var(--ink-3)] mt-1">{m.desc}</div>
              </button>
            ))}
          </div>
        )}
        {modo === 'obligatoria' && (
          <div className="flex items-start gap-2 p-2 rounded-soft bg-[rgba(168,106,6,.08)] text-[11px] text-[#A86A06]">
            <FiAlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>En modo obligatorio, los usuarios sin permiso de ubicación no podrán fichar. Asegúrate de avisarles.</span>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="ui-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="eyebrow block mb-1">Desde</label>
          <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                 className="px-3 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px]" />
        </div>
        <div>
          <label className="eyebrow block mb-1">Hasta</label>
          <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                 className="px-3 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px]" />
        </div>
        <Btn variant="ghost" onClick={cargar}>Refrescar</Btn>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-[11px] text-[var(--ink-3)]">
            {fichajes.length} marcajes con coordenadas
          </span>
          <div className="inline-flex bg-[var(--bg-2)] rounded-soft p-0.5">
            <button
              onClick={() => setVista('mapa')}
              className={`px-3 py-1 text-[11px] rounded-soft inline-flex items-center gap-1 ${
                vista === 'mapa' ? 'bg-white shadow-sm text-[var(--ink)]' : 'text-[var(--ink-3)]'
              }`}
            >
              <FiMap size={10} /> Mapa
            </button>
            <button
              onClick={() => setVista('lista')}
              className={`px-3 py-1 text-[11px] rounded-soft inline-flex items-center gap-1 ${
                vista === 'lista' ? 'bg-white shadow-sm text-[var(--ink)]' : 'text-[var(--ink-3)]'
              }`}
            >
              <FiList size={10} /> Lista
            </button>
          </div>
        </div>
      </div>

      {/* Mapa o lista */}
      <div className="ui-card overflow-hidden">
        {cargandoFichajes ? (
          <p className="p-5 text-[12px] text-[var(--ink-3)]">Cargando…</p>
        ) : fichajes.length === 0 ? (
          <p className="p-5 text-[12px] text-[var(--ink-3)]">No hay marcajes con coordenadas en este rango.</p>
        ) : vista === 'mapa' ? (
          <div className="p-3">
            <MapaFichajesLeaflet fichajes={fichajes} altura="540px" />
            <div className="mt-2 flex items-center gap-4 text-[11px] text-[var(--ink-3)]">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#2A81CB]" /> Dentro del centro
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#CB2A2A]" /> Fuera del centro
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full border border-[#2563EB]" /> Radio del centro
              </span>
            </div>
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="bg-[var(--bg-2)] border-b border-[var(--line)]">
              <tr>
                <th className="px-3 py-2 text-left text-[var(--ink-3)] uppercase text-[10px]">Fecha</th>
                <th className="px-3 py-2 text-left text-[var(--ink-3)] uppercase text-[10px]">Empleado</th>
                <th className="px-3 py-2 text-left text-[var(--ink-3)] uppercase text-[10px]">Centro</th>
                <th className="px-3 py-2 text-left text-[var(--ink-3)] uppercase text-[10px]">Entrada</th>
                <th className="px-3 py-2 text-left text-[var(--ink-3)] uppercase text-[10px]">Salida</th>
              </tr>
            </thead>
            <tbody>
              {fichajes.map((f) => (
                <tr key={f.id} className="border-b border-[var(--line)] hover:bg-[var(--bg-2)]/50">
                  <td className="px-3 py-2 text-[var(--ink)]">{f.fecha}</td>
                  <td className="px-3 py-2 text-[var(--ink)]">{f.empleado_nombre}</td>
                  <td className="px-3 py-2 text-[var(--ink-2)]">{f.centro_nombre || '—'}</td>
                  <td className="px-3 py-2"><CeldaCoords hora={f.hora_entrada} lat={f.lat_entrada} lng={f.lng_entrada} fuera={f.fuera_de_centro_entrada} /></td>
                  <td className="px-3 py-2"><CeldaCoords hora={f.hora_salida} lat={f.lat_salida} lng={f.lng_salida} fuera={f.fuera_de_centro_salida} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const CeldaCoords = ({ hora, lat, lng, fuera }) => {
  if (!hora) return <span className="text-[var(--ink-3)]">—</span>
  if (lat == null) return <span>{hora.substring(0, 5)}</span>
  return (
    <a
      href={`https://maps.google.com/?q=${lat},${lng}`}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-1 hover:underline ${fuera ? 'text-[#C13030]' : 'text-[var(--brand-1)]'}`}
      title={fuera ? 'Fuera del radio del centro' : 'Dentro del centro'}
    >
      <FiMapPin size={10} /> {hora.substring(0, 5)}
      <FiExternalLink size={9} />
    </a>
  )
}

export default MapaFichajesV2
